/**
 * Shared formatting helpers for Presence Monitoring and Sessions pages.
 *
 * Both pages read overlapping fields (check-in/out, durations, presence %)
 * for the same underlying records. Formatting them from one place is what
 * guarantees they can't drift apart — previously Presence formatted
 * timestamps in the browser's local timezone while Sessions formatted them
 * in the dashboard-selected timezone (getStoredTimezone()), so the same
 * session could show two different times on the two pages.
 */

/** Normalise an ISO timestamp so Date() always treats it as UTC/offset-aware. */
export function ensureTz(iso) {
  if (!iso) return iso;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(iso.trim())) return iso;
  return iso + 'Z';
}

/**
 * @param {string} iso
 * @param {string} [tz] - IANA timezone (e.g. from getStoredTimezone()). When
 *   omitted, formats in the viewer's browser-local timezone. Passing the
 *   dashboard-selected tz explicitly is what makes the display actually
 *   reflect it — a bare `new Date(iso).toLocaleString()` always renders in
 *   the browser's own timezone regardless of what offset the ISO string
 *   carries, since Date normalises to an absolute instant.
 */
export function formatDateTime(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(ensureTz(iso)).toLocaleString(undefined, {
      ...(tz ? { timeZone: tz } : {}),
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatTime(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(ensureTz(iso)).toLocaleTimeString(undefined, tz ? { timeZone: tz } : undefined);
  } catch {
    return iso;
  }
}

export function formatDuration(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const secs = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatPercent(value) {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return `${Math.round(value * 10) / 10}%`;
}

/**
 * Calendar-day key (YYYY-MM-DD) for an ISO timestamp, in the given IANA
 * timezone (defaults to the viewer's browser-local zone when tz is omitted
 * — pass the configured display timezone from useTimezone() for anything
 * user-facing, so the same instant always buckets into the same day
 * regardless of who's viewing it or what their browser clock is set to).
 */
export function dayKey(iso, tz) {
  if (!iso) return null;
  const d = new Date(ensureTz(iso));
  if (isNaN(d.getTime())) return null;
  if (!tz) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(d).map(p => [p.type, p.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Convert a wall-clock time (as lived in `tz`) to the equivalent UTC Date.
 * Standard technique: guess the instant assuming the wall time was UTC,
 * check what that guess actually displays as in `tz`, then correct by the
 * discovered offset.
 */
function zonedWallTimeToUtc(y, m, d, hh, mm, ss, ms, tz) {
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, ss, ms));
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(guess).map(p => [p.type, p.value])
  );
  const hour = Number(parts.hour) % 24; // some engines report midnight as "24"
  const asIfUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
                           hour, Number(parts.minute), Number(parts.second), ms);
  const offsetMs = asIfUtc - guess.getTime();
  return new Date(guess.getTime() - offsetMs);
}

/** UTC ISO instant for 00:00:00.000 on `dateStr` (YYYY-MM-DD), as lived in `tz`. */
export function zonedStartOfDay(dateStr, tz) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return zonedWallTimeToUtc(y, m, d, 0, 0, 0, 0, tz).toISOString();
}

/** UTC ISO instant for 23:59:59.999 on `dateStr` (YYYY-MM-DD), as lived in `tz`. */
export function zonedEndOfDay(dateStr, tz) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return zonedWallTimeToUtc(y, m, d, 23, 59, 59, 999, tz).toISOString();
}

export function formatDayHeading(dayKeyValue) {
  if (!dayKeyValue) return 'Unknown date';
  const [y, m, d] = dayKeyValue.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric',
  });
}
